<template>
  <el-collapse
    v-model="activeNames"
    class="calendar-collapse"
  >
    <el-collapse-item
      v-loading="isLoading && !isParentLoading"
      name="calendar"
      title="Scheduled Deliveries Calendar"
      element-loading-text="Loading Scheduled Deliveries..."
      element-loading-custom-class="custom-spinner-class"
    >
      <div class="scheduled-deliveries-calendar">
        <div class="status-container">
          <div class="active-status">
            Active
          </div>
          <div class="finished-status">
            Finished
          </div>
          <div class="cancelled-status">
            Cancelled
          </div>
        </div>

        <full-calendar
          :events="events"
          :options="calendarOptions"
          @view-render="viewRender"
          @event-selected="eventSelected"
        />
      </div>
    </el-collapse-item>
  </el-collapse>
</template>

<script lang="ts">
import { ref, Ref, reactive, watch, onMounted } from 'vue';
import _ from 'lodash';
import Moment from 'moment-timezone';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';

import util from '@/Utils/util';

const calendarOptions = {
  headerToolbar: {
    center: 'title',
    right: 'dayGridMonth,timeGridWeek,timeGridDay',
    left: 'prev,next,today',
  },
  plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin],
  initialView: 'dayGridMonth',
  contentHeight: 500,
  selectable: false,
  eventTimeFormat: {
    // like '14:30:00'
    hour: '2-digit',
    minute: '2-digit',
    meridiem: 'short',
  },
  views: {
    month: {
      // name of view
      titleFormat: {
        month: 'long',
        year: 'numeric',
      },
    },
    week: {
      titleFormat: {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      },
    },
    day: {
      titleFormat: {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      },
    },
  },
};
export default {
  name: 'DeliveriesCalendar',

  props: {
    scheduledOrders: {
      type: Array<any>,
      default: () => [],
    },
    isReloadData: {
      type: Boolean,
      default: false,
    },
    isParentLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['chooseScheduledDelivery', 'date-changed', 'choose-scheduled-delivery'],

  setup: (props: any, context: any) => {
    const { emit } = context;

    const events: Ref<any> = ref([]);
    const activeNames: Ref<any> = ref(['calendar']);
    const renderDuration = reactive({
      startDate: '',
      endDate: '',
    });
    const isLoading = ref(false);

    const loadScheduledDeliveryByDate = async () => {
      const tempEvents: any = [];

      isLoading.value = true;
      try {
        props.scheduledOrders.map((order: any) => {
          if (order.dateTimes) {
            order.dateTimes.map((dateTime: string) => {
              tempEvents.push({
                title: order.storeName,
                start: dateTime,
                end: util.formatDateTime(Moment(dateTime).add(30, 'minutes'), 'YYYY-MM-DDTHH:mm:ss'),
                allDay: false,
                deliveryOrderId: order.deliveryOrderId,
                className: getOrderStatus(order),
              });
            });
          } else {
            console.error('dateTimes not found');
          }
        });
      } catch (error) {
        console.log(error);
      }

      events.value = _.cloneDeep(tempEvents);
      isLoading.value = false;
    };

    const viewRender = (view: any) => {
      renderDuration.startDate = util.formatDateTime(view.currentStart, 'YYYY-MM-DD');
      renderDuration.endDate = util.formatDateTime(Moment(view.currentEnd).subtract(1, 'days'), 'YYYY-MM-DD');
      emit('date-changed', renderDuration);
    };

    const eventSelected = (event: any) => {
      const deliveryOrderId = _.get(event, 'extendedProps.deliveryOrderId');
      if (deliveryOrderId) {
        emit('choose-scheduled-delivery', deliveryOrderId);
      }
    };

    const getOrderStatus = (order: any) => {
      let eventName = 'active-event';

      if (order.status === 'canceled' || order.status === 'error' || order.canceledAt) {
        eventName = 'canceled-event';
      } else if (order.status === 'complete' || order.completedAt) {
        eventName = 'finished-event';
      }

      return eventName;
    };

    watch(() => props.scheduledOrders, loadScheduledDeliveryByDate);

    onMounted(loadScheduledDeliveryByDate);

    return {
      events,
      activeNames,
      renderDuration,
      isLoading,
      calendarOptions,

      loadScheduledDeliveryByDate,
      viewRender,
      eventSelected,
      getOrderStatus,
    };
  },
};
</script>

<style lang="scss">
.calendar-collapse {
  &.el-collapse {
    border: 0;
    margin-bottom: 30px;
    .el-collapse-item__header {
      &.is-active {
        border-bottom: 1px solid #c7963c;
      }
      font-size: 20px;
      background-color: #b37a0b;
      color: #fff;
      padding-left: 10px;
      border-bottom: 0;
      @media (max-width: 576px) {
        font-size: 16px;
        padding-left: 10px;
      }
    }
    .el-collapse-item {
      border: 1px solid #a77111;
    }
    .el-collapse-item__content {
      padding-bottom: 0;
    }
    .el-collapse-item__wrap {
      background-color: transparent;
    }
    .el-loading-mask {
      background-color: rgba(29, 28, 28, 0.6) !important;
    }
    .el-loading-spinner {
      .el-loading-text,
      .path {
        color: #2bf108;
        stroke: #2bf108;
        font-size: 16px;
      }
    }
  }
}
.scheduled-deliveries-calendar {
  position: relative;
  padding: 15px 10px;
  z-index: 0;
  @media (max-width: 576px) {
    padding: 5px 10px;
  }
  .status-container {
    display: flex;
    position: absolute;
    top: 50px;
    padding: 5px;
    border: 1px dotted #bc8111;
    @media (max-width: 576px) {
      left: 0;
      top: 80px;
      padding: 2px;
      border: 0;
    }
    div {
      width: 80px;
      height: 20px;
      text-align: center;
      margin-left: 5px;
      margin-right: 5px;
      color: #fff;
      border-radius: 5px;
      font-size: 12px;
      line-height: 20px;
      &:first-child {
        margin-left: 0;
      }
      &:last-child {
        margin-right: 0;
      }
      &.active-status {
        background-color: #4caf50;
        // box-shadow: 0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px rgba(76,175,80,.4);
      }
      &.finished-status {
        background-color: #00bcd4;
        // box-shadow: 0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px rgba(0,188,212,.4);
      }
      &.cancelled-status {
        background-color: #e91e63;
        // box-shadow: 0 4px 20px 0 rgba(0,0,0,.14), 0 7px 10px -5px rgba(233,30,99,.4);
      }
    }
  }
  .fc-toolbar.fc-header-toolbar {
    margin-bottom: 50px !important;
    @media (max-width: 576px) {
      position: relative;
      .fc-left {
        position: absolute;
        min-width: unset;
        left: -5px;
      }
      .fc-right {
        position: absolute;
        min-width: unset;
        right: -5px;
      }
      .fc-center {
        margin-top: 35px;
        margin-bottom: 10px;
        h2 {
          font-size: 24px;
        }
      }
      .fc-button-group {
        button {
          padding-left: 5px !important;
          padding-right: 5px !important;
        }
      }
    }
  }
  .fc-today-button {
    text-transform: capitalize;
  }
  .fc-button {
    padding-top: 0;
    font-size: 13px;
    padding-bottom: 0;
    line-height: 25px;
    height: 28px;
  }
  .fc-state-default {
    background-image: none;
    box-shadow: none;
    border-radius: 0;
    background-color: #208080;
    border-color: #4caf50;
    min-width: unset;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    margin-right: 3px !important;
  }
  .fc .fc-state-active,
  .fc .fc-state-active:focus,
  .fc .fc-state-active:hover,
  .fc .fc-state-active:active:focus,
  .fc .fc-state-active:active:hover,
  .fc .fc-state-active:active {
    background-color: #4caf50;
  }
  .fc button[disabled],
  .fc button[disabled]:focus,
  .fc button[disabled]:hover {
    background-color: #4caf50;
    color: #fff;
    border-color: #4caf50;
  }
  .fc-event {
    border: transparent;
    color: #fff !important;
    &.active-event {
      background-color: #4caf50;
      box-shadow:
        0 4px 20px 0 rgba(0, 0, 0, 0.14),
        0 7px 10px -5px rgba(76, 175, 80, 0.4);
    }
    &.finished-event {
      background-color: #00bcd4;
      box-shadow:
        0 4px 20px 0 rgba(0, 0, 0, 0.14),
        0 7px 10px -5px rgba(0, 188, 212, 0.4);
    }
    &.canceled-event {
      background-color: #e91e63;
      box-shadow:
        0 4px 20px 0 rgba(0, 0, 0, 0.14),
        0 7px 10px -5px rgba(233, 30, 99, 0.4);
    }
    &:hover {
      cursor: pointer;
    }
  }
  .fc-time {
    text-transform: uppercase;
  }
  .fc-day-grid-event .fc-content,
  .fc-time-grid .fc-event,
  .fc-time-grid .fc-bgevent {
    cursor: pointer;
  }
  .fc-agendaDay-view {
    .fc-time-grid-event {
      .fc-content {
        padding-top: 1px;
        font-size: 16px;
      }
      .fc-time {
        font-weight: bold;
      }
    }
  }
  .fc-agendaWeek-view {
    .fc-time-grid-event {
      .fc-content {
        padding-top: 4px;
      }
      .fc-time {
        font-weight: bold;
        font-size: 12px;
      }
      .fc-title {
        font-size: 12px;
      }
    }
  }
}
</style>
